import type { StudioRoute } from '@/store/types'
import {
  STUDIO_ROUTE_CANONICAL_PATH_BY_ROUTE,
  STUDIO_ROUTE_REGISTRY,
} from './studio-route-registry'

const STUDIO_NEW_WORK_PATH = '/studio/new'
const STUDIO_HOME_PATH = '/studio'
const STUDIO_NEW_WORK_RETURN_STATE_KEY = 'cogoStudioNewWorkReturnTo'

const STUDIO_NEW_WORK_RETURN_PATHS: ReadonlySet<string> = new Set(
  STUDIO_ROUTE_REGISTRY.filter(
    (entry) =>
      !Object.prototype.hasOwnProperty.call(entry, 'legacyAliasFor') &&
      entry.route !== 'new-project',
  ).map((entry) => entry.canonicalPath),
)

function canonicalNewWorkReturnPath(candidate: unknown): string | null {
  return typeof candidate === 'string' &&
    STUDIO_NEW_WORK_RETURN_PATHS.has(candidate)
    ? candidate
    : null
}

function isNewWorkPath(path: string): boolean {
  try {
    const url = new URL(path, window.location.href)
    return (
      url.origin === window.location.origin &&
      url.pathname === STUDIO_NEW_WORK_PATH
    )
  } catch {
    return false
  }
}

export function studioRoutePath(route: StudioRoute | 'home'): string {
  return STUDIO_ROUTE_CANONICAL_PATH_BY_ROUTE[route] ?? (route === 'home' ? '/studio' : `/studio/${route}`)
}

export function pushStudioRoute(route: StudioRoute | 'home'): void {
  if (typeof window === 'undefined') return
  if (route === 'new-project') {
    pushStudioPath(studioRoutePath(route))
    return
  }
  window.history.pushState(null, '', studioRoutePath(route))
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export function replaceStudioRoute(route: StudioRoute | 'home'): void {
  if (typeof window === 'undefined') return
  window.history.replaceState(null, '', studioRoutePath(route))
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export function pushStudioPath(path: string): void {
  if (typeof window === 'undefined') return

  if (isNewWorkPath(path)) {
    const currentPath = canonicalNewWorkReturnPath(window.location.pathname)
    const returnTo = currentPath ?? STUDIO_HOME_PATH

    // A legacy alias or unknown address must not become a trusted return
    // destination. Canonicalise the entry underneath the wizard first so a
    // later history traversal lands on the fail-closed Home destination.
    if (!currentPath) {
      window.history.replaceState(null, '', returnTo)
    }

    window.history.pushState(
      { [STUDIO_NEW_WORK_RETURN_STATE_KEY]: returnTo },
      '',
      path,
    )
    window.dispatchEvent(new PopStateEvent('popstate'))
    return
  }

  window.history.pushState(null, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export function cancelStudioNewWork(): void {
  if (typeof window === 'undefined') return

  const returnTo = canonicalNewWorkReturnPath(
    window.history.state?.[STUDIO_NEW_WORK_RETURN_STATE_KEY],
  )

  if (returnTo) {
    window.history.back()
    return
  }

  window.history.replaceState(null, '', STUDIO_HOME_PATH)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
